import { useCallback, useEffect, useState } from 'react'
import {
  fetchMovieDetails,
  fetchSeriesDetails,
  type TmdbMovieDetails,
  type TmdbSeriesDetails,
} from '../tmdb/tmdb'
import { getEffectiveTmdbApiKey, useUserSettings } from '../settings/useUserSettings'

const ROOT_MARGIN = '400px'
// Bounded retry schedule for transient TMDB failures (rate limit / flaky
// network). The earlier implementation gave up after a single failed
// fetch and never retried, which manifested as cards permanently missing
// their rating + poster — see fix/pwa-ratings-sync.
const RETRY_DELAYS_MS = [1500, 4000, 10000]

/**
 * Per-card lazy TMDB movie fetch.
 *
 * The library grid has hundreds of cards but only ~20 are visible at a
 * time. Instead of firing every lookup on mount (which trips TMDB rate
 * limits and makes everything fight for slots), each card attaches an
 * IntersectionObserver and only fetches once it enters the viewport.
 *
 * The observer lives inside a useEffect that depends on the DOM element
 * AND the API key. That matters because the Firestore settings doc can
 * arrive AFTER the library grid paints — without the dependency on the
 * key, the observer captures an undefined key in its closure, fires on
 * scroll, bails out of the fetch, and leaves the card permanently blank.
 * Re-running the effect when the key arrives rebuilds the observer with
 * a fresh closure.
 *
 * If the fetch resolves to `null` (transient TMDB failure / rate limit),
 * we schedule bounded retries with exponential backoff so cards aren't
 * permanently stuck without a rating or poster. The cache also evicts
 * null promises in `tmdb.ts`, so retry calls actually re-hit the network
 * rather than pulling a stale "no data" promise out of the cache.
 */
export function useTmdbLazyMovie(tmdbId: number): {
  details: TmdbMovieDetails | undefined
  loading: boolean
  ref: (el: HTMLElement | null) => void
} {
  const { settings } = useUserSettings()
  const [details, setDetails] = useState<TmdbMovieDetails | undefined>()
  const [loading, setLoading] = useState(false)
  const [element, setElement] = useState<HTMLElement | null>(null)

  const ref = useCallback((el: HTMLElement | null) => {
    setElement(el)
  }, [])

  // Falls back to the bundled developer key (when one was baked into the
  // build via VITE_TMDB_BUNDLED_API_KEY) so cards render without the user
  // having to set up TMDB themselves.
  const apiKey = getEffectiveTmdbApiKey(settings)
  const language = settings?.tmdbPreferredLanguage || 'en'

  useEffect(() => {
    if (!element || details || !apiKey) return

    let cancelled = false
    let retryHandle: ReturnType<typeof setTimeout> | null = null

    const tryFetch = async (attempt: number): Promise<void> => {
      if (cancelled) return
      setLoading(true)
      const d = await fetchMovieDetails(tmdbId, apiKey, language)
      if (cancelled) return
      if (d) {
        setDetails(d)
        setLoading(false)
        return
      }
      // Transient failure — schedule a retry if we have budget left.
      if (attempt < RETRY_DELAYS_MS.length) {
        const delay = RETRY_DELAYS_MS[attempt]
        retryHandle = setTimeout(() => {
          retryHandle = null
          void tryFetch(attempt + 1)
        }, delay)
      } else {
        setLoading(false)
      }
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          observer.disconnect()
          void tryFetch(0)
        }
      },
      { rootMargin: ROOT_MARGIN }
    )
    observer.observe(element)

    return () => {
      cancelled = true
      observer.disconnect()
      if (retryHandle != null) clearTimeout(retryHandle)
    }
  }, [element, details, apiKey, language, tmdbId])

  return { details, loading, ref }
}

/**
 * Same lifecycle (and same retry policy) for series. Kept as a separate
 * hook so the underlying cache stays type-distinct from movies.
 */
export function useTmdbLazySeries(tmdbId: number): {
  details: TmdbSeriesDetails | undefined
  loading: boolean
  ref: (el: HTMLElement | null) => void
} {
  const { settings } = useUserSettings()
  const [details, setDetails] = useState<TmdbSeriesDetails | undefined>()
  const [loading, setLoading] = useState(false)
  const [element, setElement] = useState<HTMLElement | null>(null)

  const ref = useCallback((el: HTMLElement | null) => {
    setElement(el)
  }, [])

  // Falls back to the bundled developer key (when one was baked into the
  // build via VITE_TMDB_BUNDLED_API_KEY) so cards render without the user
  // having to set up TMDB themselves.
  const apiKey = getEffectiveTmdbApiKey(settings)
  const language = settings?.tmdbPreferredLanguage || 'en'

  useEffect(() => {
    if (!element || details || !apiKey) return

    let cancelled = false
    let retryHandle: ReturnType<typeof setTimeout> | null = null

    const tryFetch = async (attempt: number): Promise<void> => {
      if (cancelled) return
      setLoading(true)
      const d = await fetchSeriesDetails(tmdbId, apiKey, language)
      if (cancelled) return
      if (d) {
        setDetails(d)
        setLoading(false)
        return
      }
      if (attempt < RETRY_DELAYS_MS.length) {
        const delay = RETRY_DELAYS_MS[attempt]
        retryHandle = setTimeout(() => {
          retryHandle = null
          void tryFetch(attempt + 1)
        }, delay)
      } else {
        setLoading(false)
      }
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          observer.disconnect()
          void tryFetch(0)
        }
      },
      { rootMargin: ROOT_MARGIN }
    )
    observer.observe(element)

    return () => {
      cancelled = true
      observer.disconnect()
      if (retryHandle != null) clearTimeout(retryHandle)
    }
  }, [element, details, apiKey, language, tmdbId])

  return { details, loading, ref }
}
